import { useState, useEffect } from 'react'
import { ClipboardList, Plus, Eye, Edit3, Trash2, PenTool, ChevronDown, ChevronRight } from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../Toast'
import ConfirmModal from '../ConfirmModal'
import type { SchedaIstanzaList } from '../../types/schede'
import { STATO_ISTANZA_CONFIG } from '../../types/schede'
import SchedaIstanzaModal from './SchedaIstanzaModal'
import SchedaIstanzaDetail from './SchedaIstanzaDetail'

interface SchedePerEntitaProps {
  entityType: string
  entityId: string
  entityLabel?: string
}

export default function SchedePerEntita({ entityType, entityId, entityLabel }: SchedePerEntitaProps) {
  const [istanze, setIstanze] = useState<SchedaIstanzaList[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [selectedIstanzaId, setSelectedIstanzaId] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    loadIstanze()
  }, [entityType, entityId])

  const loadIstanze = async () => {
    try {
      const res = await api.get(`/v1/schede-dinamiche/per-entita/${entityType}/${entityId}`)
      setIstanze(res.data)
    } catch {
      // Silenzioso se non ci sono schede
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteId) return
    try {
      await api.delete(`/v1/schede-dinamiche/istanze/${deleteId}`)
      setToast({ message: 'Scheda eliminata', type: 'success' })
      loadIstanze()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore eliminazione', type: 'error' })
    } finally {
      setDeleteId(null)
    }
  }

  if (loading) return null

  const formatDate = (iso: string | null) => {
    if (!iso) return '-'
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-'
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <div className="mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-sm font-semibold"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <ClipboardList className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
          Schede ({istanze.length})
        </button>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors"
          style={{ color: 'var(--color-primary)' }}
        >
          <Plus className="w-3 h-3" /> Nuova scheda
        </button>
      </div>

      {/* Tabella storico istanze */}
      {expanded && (
        istanze.length === 0 ? (
          <p className="text-xs py-2" style={{ color: 'var(--color-text-tertiary)' }}>
            Nessuna scheda compilata
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-elevated, var(--color-bg-card))' }}>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Template</th>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Codice</th>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Stato</th>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Compilatore</th>
                  <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Data</th>
                  <th className="px-3 py-2 text-right font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {istanze.map(ist => {
                  const stato = STATO_ISTANZA_CONFIG[ist.stato] || STATO_ISTANZA_CONFIG.bozza
                  const isViewOnly = ist.edit_policy === 'view_only' && ist.stato !== 'bozza'
                  const isLocked = ist.stato === 'firmata' || ist.stato === 'archiviata'

                  return (
                    <tr
                      key={ist.id}
                      className="border-t cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}
                      onClick={() => setSelectedIstanzaId(ist.id)}
                    >
                      <td className="px-3 py-2 font-medium">{ist.template_nome}</td>
                      <td className="px-3 py-2" style={{ color: 'var(--color-text-tertiary)' }}>{ist.codice}</td>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-1">
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${stato.bg} ${stato.color}`}>
                            {stato.label}
                          </span>
                          {ist.has_firma && (
                            <span title="Firmata"><PenTool className="w-3 h-3" style={{ color: 'var(--color-success, #22c55e)' }} /></span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2" style={{ color: 'var(--color-text-secondary)' }}>
                        {ist.compilato_da_nome || '-'}
                      </td>
                      <td className="px-3 py-2" style={{ color: 'var(--color-text-tertiary)' }}>
                        {ist.completata_at ? formatDateTime(ist.completata_at) : formatDate(ist.created_at)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedIstanzaId(ist.id)}
                            className="p-1 rounded hover:opacity-80"
                            style={{ color: 'var(--color-primary)' }}
                            title={isViewOnly || isLocked ? 'Visualizza' : 'Modifica'}
                          >
                            {isViewOnly || isLocked
                              ? <Eye className="w-3.5 h-3.5" />
                              : <Edit3 className="w-3.5 h-3.5" />
                            }
                          </button>
                          {!isLocked && (
                            <button
                              onClick={() => setDeleteId(ist.id)}
                              className="p-1 rounded hover:opacity-80"
                              style={{ color: 'var(--color-danger, #ef4444)' }}
                              title="Elimina"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Compact view when collapsed (just show count) */}
      {!expanded && istanze.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {istanze.slice(0, 3).map(ist => {
            const stato = STATO_ISTANZA_CONFIG[ist.stato] || STATO_ISTANZA_CONFIG.bozza
            return (
              <button
                key={ist.id}
                onClick={() => setSelectedIstanzaId(ist.id)}
                className="flex items-center gap-1 px-2 py-1 rounded border text-[10px] hover:opacity-80 transition-opacity"
                style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}
              >
                <span className="font-medium">{ist.template_nome}</span>
                <span className={`px-1 py-0.5 rounded-full ${stato.bg} ${stato.color}`}>{stato.label}</span>
              </button>
            )
          })}
          {istanze.length > 3 && (
            <button
              onClick={() => setExpanded(true)}
              className="px-2 py-1 text-[10px]"
              style={{ color: 'var(--color-primary)' }}
            >
              +{istanze.length - 3} altre...
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <SchedaIstanzaModal
          entityType={entityType}
          entityId={entityId}
          entityLabel={entityLabel}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false)
            loadIstanze()
            setExpanded(true) // expand to show the new instance
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedIstanzaId && (
        <SchedaIstanzaDetail
          istanzaId={selectedIstanzaId}
          onClose={() => {
            setSelectedIstanzaId(null)
            loadIstanze()
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Eliminare scheda"
        message="Eliminare questa scheda compilata?"
        confirmLabel="Elimina"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
